'use client';

import React, { useCallback, useRef, useState } from 'react';
import { mutate } from 'swr';
import addPostRating, { RatingError, RatingResponse } from '@/utils/post/AddPostRating';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import LoginPrompt, {
    LOGIN_DIALOG_CLASS,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/LoginPrompt';

const anonymousId = () => {
    try {
        let id = localStorage.getItem('user_rated_id');
        if (!id) {
            id = Math.random().toString(36).substring(2, 30) + Date.now();
            localStorage.setItem('user_rated_id', id);
        }
        return id;
    } catch {
        return '';
    }
};

/**
 * Saving a rating needs a login (the API answers 401 otherwise). Instead of failing silently,
 * submit() opens a login dialog and, once the visitor is logged in, sends the same rating, so
 * the star they picked is not lost. Render `dialog` once next to the stars.
 */
export function useSubmitRating(postId: number) {
    const { isLoading, isLoggedInGlobal } = useVerifyUser();
    const [loginOpen, setLoginOpen] = useState(false);
    const [pendingRating, setPendingRating] = useState(0);
    const pending = useRef<{
        rating: number;
        resolve: (r: RatingResponse) => void;
        reject: (e: unknown) => void;
    } | null>(null);

    const askLogin = useCallback(
        (rating: number) =>
            new Promise<RatingResponse>((resolve, reject) => {
                pending.current = { rating, resolve, reject };
                setPendingRating(rating);
                setLoginOpen(true);
            }),
        [],
    );

    const submit = useCallback(
        (rating: number): Promise<RatingResponse> => {
            if (!isLoading && !isLoggedInGlobal) return askLogin(rating);
            return addPostRating(postId, anonymousId(), rating).catch((error) => {
                if (error instanceof RatingError && error.status === 401) return askLogin(rating);
                throw error;
            });
        },
        [postId, isLoading, isLoggedInGlobal, askLogin],
    );

    const handleLoginSuccess = useCallback(async () => {
        await mutate('verifyUser');
        const job = pending.current;
        pending.current = null;
        setLoginOpen(false);
        if (job) addPostRating(postId, anonymousId(), job.rating).then(job.resolve, job.reject);
    }, [postId]);

    const handleOpenChange = useCallback((open: boolean) => {
        setLoginOpen(open);
        if (!open && pending.current) {
            pending.current.reject(new RatingError('برای ثبت امتیاز باید وارد شوید.', 401));
            pending.current = null;
        }
    }, []);

    const dialog = (
        <Dialog open={loginOpen} onOpenChange={handleOpenChange}>
            <DialogContent className={LOGIN_DIALOG_CLASS}>
                <LoginPrompt
                    reason='برای ثبت امتیاز وارد شوید'
                    hint={
                        pendingRating > 0
                            ? `امتیاز ${pendingRating} ستاره‌ی شما بلافاصله بعد از ورود ثبت می‌شود.`
                            : 'بعد از ورود، امتیاز شما ثبت می‌شود.'
                    }
                    onSuccess={handleLoginSuccess}
                />
            </DialogContent>
        </Dialog>
    );

    return { submit, dialog, loginOpen };
}
